import { createContext, useContext } from 'react';

export type Density = 'compact' | 'default' | 'comfortable';

/* Context for Form → Field density propagation. When a FormField sets
   a density, nested Field components inherit it automatically unless
   they declare an explicit density prop of their own. Components
   outside a FormDensityContext.Provider receive 'default'.

   Public API — the return type is intentionally `Density` (not
   `Density | undefined`) so adopters who switch on the value don't
   need a null branch. Codex audit #108 R7 → R9: an earlier iteration
   broadened this to `Density | undefined` for the ambient-fallthrough
   distinction, but that's a public type break. The distinction now
   lives on the internal FormDensityExplicitContext (below) which is
   NOT exported from `@atelierpx/jacquard/kit`.

   Known limitation (codex #108 R14): using this context's `.Provider`
   directly with `value="default"` to opt a subtree out of ambient
   density does NOT work — direct context use can't set the internal
   "explicit" flag, and `value="default"` is indistinguishable from
   "no provider." The recommended path for explicit default is the
   per-component prop:
     <Field density="default" />
     <Table density="default" />
   FormField wraps both contexts correctly, so `<FormField
   density="default">` is also a supported opt-out. */
export const FormDensityContext = createContext<Density>('default');

export function useFormDensity(): Density {
  return useContext(FormDensityContext);
}

/* Internal-only — tracks whether a wrapping FormField actually
   provided a density prop. Field reads this to distinguish:
   - true  → an explicit Form density is in effect (compact/comfortable
            /default were all explicitly chosen) → render data-density
            so ambient cascade opts out.
   - false → no Form ancestor set density → fall through to ambient.
   Not exported; lives alongside the public FormDensityContext. */
export const FormDensityExplicitContext = createContext<boolean>(false);

export function useFormDensityExplicit(): boolean {
  return useContext(FormDensityExplicitContext);
}
